import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import { Language } from '../App';
interface FAQProps {
  language: Language;
}
const content = {
  en: {
    title: 'Frequently Asked Questions',
    faqs: [{
      question: 'How do I schedule an appointment?',
      answer: 'Submit a consultation request through our form. Our coordinator will contact you within 24 hours to schedule your private appointment at a time that works for you.'
    }, {
      question: 'What services do you provide?',
      answer: 'We offer comprehensive medical consultations with board-certified physicians. Services include general medical care, specialized consultations, and follow-up appointments. All services are provided in a private, confidential setting.'
    }, {
      question: 'Do you accept insurance?',
      answer: 'We are a private practice and do not directly bill insurance. However, we can provide documentation for you to submit to your insurance provider for potential reimbursement.'
    }, {
      question: 'How is my privacy protected?',
      answer: 'We are fully HIPAA-compliant and maintain the strictest standards of patient confidentiality. All consultations are conducted in private settings, and your medical information is securely protected.'
    }, {
      question: 'Do you offer services in Spanish?',
      answer: 'Yes, we provide medical services in both English and Spanish. You can indicate your language preference when submitting your consultation request.'
    }, {
      question: 'Where are you located?',
      answer: 'We are based in New York and serve patients throughout the state. Specific location details will be provided when your appointment is confirmed.'
    }]
  },
  es: {
    title: 'Preguntas Frecuentes',
    faqs: [{
      question: '¿Cómo programo una cita?',
      answer: 'Envíe una solicitud de consulta a través de nuestro formulario. Nuestro coordinador se comunicará con usted en 24 horas para programar su cita privada en un momento que le convenga.'
    }, {
      question: '¿Qué servicios proporcionan?',
      answer: 'Ofrecemos consultas médicas integrales con médicos certificados. Los servicios incluyen atención médica general, consultas especializadas y citas de seguimiento. Todos los servicios se brindan en un entorno privado y confidencial.'
    }, {
      question: '¿Aceptan seguro?',
      answer: 'Somos una práctica privada y no facturamos directamente al seguro. Sin embargo, podemos proporcionar documentación para que la presente a su proveedor de seguros para un posible reembolso.'
    }, {
      question: '¿Cómo se protege mi privacidad?',
      answer: 'Cumplimos totalmente con HIPAA y mantenemos los estándares más estrictos de confidencialidad del paciente. Todas las consultas se realizan en entornos privados y su información médica está protegida de forma segura.'
    }, {
      question: '¿Ofrecen servicios en español?',
      answer: 'Sí, proporcionamos servicios médicos tanto en inglés como en español. Puede indicar su preferencia de idioma al enviar su solicitud de consulta.'
    }, {
      question: '¿Dónde están ubicados?',
      answer: 'Estamos ubicados en Nueva York y atendemos a pacientes en todo el estado. Los detalles específicos de la ubicación se proporcionarán cuando se confirme su cita.'
    }]
  }
};
export function FAQ({
  language
}: FAQProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const t = content[language];
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return <section ref={ref} className="py-24 px-4 sm:px-6 lg:px-8 bg-espresso" data-id="element-69">
      <div className="max-w-4xl mx-auto" data-id="element-70">
        <motion.h2 initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-4xl sm:text-5xl font-bold text-pearl text-center mb-16" data-id="element-71">
          {t.title}
        </motion.h2>

        <div className="space-y-4" data-id="element-72">
          {t.faqs.map((faq, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} className="bg-brown-dark/50 rounded-xl overflow-hidden border border-pearl/10" data-id="element-73">
              <button onClick={() => setOpenIndex(openIndex === index ? null : index)} className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-pearl/5 transition-colors" data-id="element-74">
                <span className="text-lg font-semibold text-pearl pr-4" data-id="element-75">
                  {faq.question}
                </span>
                <motion.div animate={{
              rotate: openIndex === index ? 180 : 0
            }} transition={{
              duration: 0.3
            }} data-id="element-76">
                  <ChevronDownIcon className="w-6 h-6 text-pearl flex-shrink-0" data-id="element-77" />
                </motion.div>
              </button>

              <AnimatePresence data-id="element-78">
                {openIndex === index && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3
            }} className="overflow-hidden" data-id="element-79">
                    <div className="px-6 pb-5 text-pearl/70 leading-relaxed" data-id="element-80">
                      {faq.answer}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>)}
        </div>
      </div>
    </section>;
}